import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';

/**
 * Toast Notification System
 * Replaces window.alert() with styled, non-blocking notifications
 *
 * Usage with Context:
 *
 * // Wrap app with ToastProvider:
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Use in any component:
 * const { showToast } = useToast();
 * showToast({ message: 'Success!', type: 'success' });
 */

const TOAST_TYPES = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-brand-50',
    borderColor: 'border-brand-200',
    iconColor: 'text-brand-500',
    textColor: 'text-brand-800',
    progressColor: 'bg-brand-500',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-500',
    textColor: 'text-red-800',
    progressColor: 'bg-red-500',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    iconColor: 'text-yellow-500',
    textColor: 'text-yellow-800',
    progressColor: 'bg-yellow-500',
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-500',
    textColor: 'text-blue-800',
    progressColor: 'bg-blue-500',
  },
};

// Individual Toast Component
const Toast = ({ id, message, type = 'info', duration = 5000, onClose, title }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const config = TOAST_TYPES[type] || TOAST_TYPES.info;
  const IconComponent = config.icon;

  useEffect(() => {
    if (duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        handleClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 200);
  }, [id, onClose]);

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl border shadow-lg max-w-sm w-full
        transform transition-all duration-200 ease-out
        ${config.bgColor} ${config.borderColor}
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
      `}
      role="alert"
      aria-live="assertive"
    >
      <div className="p-4 flex items-start gap-3">
        <IconComponent className={`w-5 h-5 flex-shrink-0 mt-0.5 ${config.iconColor}`} />

        <div className="flex-1 min-w-0">
          {title && (
            <h4 className={`font-semibold text-sm mb-0.5 ${config.textColor}`}>
              {title}
            </h4>
          )}
          <p className={`text-sm ${config.textColor}`}>
            {message}
          </p>
        </div>

        <button
          onClick={handleClose}
          className={`flex-shrink-0 p-1 rounded-lg hover:bg-black/5 transition-colors ${config.textColor}`}
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5">
          <div
            className={`h-full transition-all duration-100 ${config.progressColor}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast, position = 'top-right' }) => {
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  return (
    <div
      className={`fixed z-[100] flex flex-col gap-2 ${positionClasses[position] || positionClasses['top-right']}`}
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={removeToast} />
      ))}
    </div>
  );
};

// Toast Context
const ToastContext = createContext(null);

// Toast Provider Component
export const ToastProvider = ({ children, position = 'top-right', maxToasts = 5 }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({
    message,
    type = 'info',
    duration = 5000,
    title,
  }) => {
    const id = Date.now() + Math.random();

    setToasts((prev) => {
      const newToasts = [...prev, { id, message, type, duration, title }];
      // Keep only the most recent toasts
      return newToasts.slice(-maxToasts);
    });

    return id;
  }, [maxToasts]);

  // Convenience methods
  const toast = {
    show: showToast,
    success: (message, options = {}) => showToast({ message, type: 'success', ...options }),
    error: (message, options = {}) => showToast({ message, type: 'error', ...options }),
    warning: (message, options = {}) => showToast({ message, type: 'warning', ...options }),
    info: (message, options = {}) => showToast({ message, type: 'info', ...options }),
  };

  return (
    <ToastContext.Provider value={{ showToast, toast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} position={position} />
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default Toast;
